import { useState } from "react";

// ─── STATIC DUMMY DATA ───────────────────────────────────────────
const CONTACT = {
    firstName: "John",
    name: "Smith",
    privateEmail: "john.smith@email.com",
    privateMobile: "+32 470 00 00 00",
};

const ESTATE = {
    id: "EST-10042",
    name: "Modern Apartment – Ixelles",
    address: "Rue de la Loi",
    number: "42",
    zip: "1000",
    city: "Brussels",
    rooms: 3,
    bathrooms: 2,
    area: "95 m²",
    imageUrl: "https://placehold.co/160x105/e2e8f0/94a3b8?text=Property",
    officeLogo: "https://placehold.co/180x48/274b97/ffffff?text=Office+Logo",
    waitingListUrl: "#",
};

const EVENT = {
    date: "12 / 06 / 2025",
    time: "10:00 AM",
};

// ─── TRANSLATIONS ────────────────────────────────────────────────
const translations = {
    "en-GB": {
        Dear: "Dear",
        Resume: "RESUME OF YOUR VISIT REQUEST",
        Reason: "Reason:",
        VisitDetails: "Visit Details",
        Date: "Date:",
        Time: "Time:",
        PropertyRecap: "Property Recap:",
        ContactDetails: "Contact Details",
        RemoveBtn: "Remove from waiting list",
    },
    "fr-BE": {
        Dear: "Bonjour",
        Resume: "RÉSUMÉ DE VOTRE DEMANDE",
        Reason: "Raison :",
        VisitDetails: "Détails de la visite",
        Date: "Date :",
        Time: "Heure :",
        PropertyRecap: "Récapitulatif du bien",
        ContactDetails: "Coordonnées",
        RemoveBtn: "Se retirer de la liste",
    },
    "nl-BE": {
        Dear: "Beste",
        Resume: "SAMENVATTING VAN UW AANVRAAG",
        Reason: "Reden:",
        VisitDetails: "Bezoekdetails",
        Date: "Datum:",
        Time: "Tijd:",
        PropertyRecap: "Woning overzicht",
        ContactDetails: "Contactgegevens",
        RemoveBtn: "Verwijderen van wachtlijst",
    },
};

// ─── STATIC CONTENT PER SCENARIO ────────────────────────────────
const content = {
    // scenario key → { subheading, reasons[], showRemoveBtn, showVisitDetails }

    "OnHold_waiting": {
        "en-GB": {
            subheading: "The property you are interested in is currently under offer and visits are temporarily suspended.",
            reasons: [
                "The owner has accepted an offer for the property. While the sale or rental process is ongoing, visits are temporarily unavailable.",
                "You remain registered on the waiting list. If the property becomes available again, you will automatically be notified and given the opportunity to book a visit.",
            ],
        },
        "fr-BE": {
            subheading: "Le bien est sous offre et les visites sont suspendues.",
            reasons: [
                "Le propriétaire a accepté une offre pour le bien. Pendant le processus de vente ou de location, les visites sont temporairement indisponibles.",
                "Vous restez inscrit sur la liste d'attente. Si le bien redevient disponible, vous serez automatiquement informé et pourrez réserver une visite.",
            ],
        },
        "nl-BE": {
            subheading: "De woning staat onder bod en bezichtigingen zijn tijdelijk stopgezet.",
            reasons: [
                "De eigenaar heeft een bod geaccepteerd. Tijdens het verkoop- of verhuurproces zijn bezichtigingen tijdelijk niet beschikbaar.",
                "U blijft geregistreerd op de wachtlijst. Als de woning opnieuw beschikbaar wordt, wordt u automatisch op de hoogte gebracht en kunt u een bezoek boeken.",
            ],
        },
        showRemoveBtn: false,
        showVisitDetails: false,
    },

    "Deactive_waiting": {
        "en-GB": {
            subheading: "The property you were interested in is no longer available for visits.",
            reasons: ["The property has been sold, rented, or removed from the market."],
        },
        "fr-BE": {
            subheading: "Le bien qui vous intéressait n'est plus disponible pour les visites.",
            reasons: ["Le bien a été vendu, loué ou retiré du marché."],
        },
        "nl-BE": {
            subheading: "De woning waarin u geïnteresseerd was, is niet langer beschikbaar voor bezoeken.",
            reasons: ["De woning is verkocht, verhuurd of van de markt gehaald."],
        },
        showRemoveBtn: false,
        showVisitDetails: false,
    },

    "OnHold_pending": {
        "en-GB": {
            subheading: `Your visit request for the property ${ESTATE.id} has been cancelled because the property is currently under offer.`,
            reasons: [
                "An offer has been accepted by the owner and visits are temporarily suspended while the transaction is being processed.",
                "You have been automatically added to the waiting list. If the property becomes available again, you will be notified.",
                "If you no longer wish to remain on the waiting list, you may remove yourself using the button below.",
            ],
        },
        "fr-BE": {
            subheading: `Votre demande de visite pour le bien ${ESTATE.id} a été annulée car le bien est sous offre.`,
            reasons: [
                "Une offre a été acceptée par le propriétaire et les visites sont suspendues pendant le traitement de la transaction.",
                "Vous avez été automatiquement ajouté à la liste d'attente. Si le bien redevient disponible, vous serez informé.",
                "Si vous ne souhaitez plus rester sur la liste d'attente, vous pouvez vous en retirer via le bouton ci-dessous.",
            ],
        },
        "nl-BE": {
            subheading: `Uw aanvraag voor woning ${ESTATE.id} is geannuleerd omdat de woning onder bod staat.`,
            reasons: [
                "Er is een bod geaccepteerd en bezichtigingen zijn tijdelijk opgeschort terwijl de transactie wordt verwerkt.",
                "U bent automatisch toegevoegd aan de wachtlijst. Als de woning opnieuw beschikbaar wordt, wordt u op de hoogte gebracht.",
                "Als u niet langer op de wachtlijst wilt blijven, kunt u uzelf verwijderen via de onderstaande knop.",
            ],
        },
        showRemoveBtn: true,
        showVisitDetails: true,
    },

    "Deactive_pending": {
        "en-GB": {
            subheading: `Your visit request for the property ${ESTATE.id} has been cancelled.`,
            reasons: ["The property is no longer available because it has been sold, rented, or removed from the market."],
        },
        "fr-BE": {
            subheading: `Votre demande de visite pour le bien ${ESTATE.id} a été annulée.`,
            reasons: ["Le bien n'est plus disponible car il a été vendu, loué ou retiré du marché."],
        },
        "nl-BE": {
            subheading: `Uw bezoekaanvraag voor de woning ${ESTATE.id} is geannuleerd.`,
            reasons: ["De woning is niet langer beschikbaar omdat deze is verkocht, verhuurd of van de markt gehaald."],
        },
        showRemoveBtn: false,
        showVisitDetails: true,
    },

    "OnHold_confirm": {
        "en-GB": {
            subheading: `Your confirmed visit for the property ${ESTATE.id} scheduled on ${EVENT.date} at ${EVENT.time} has been cancelled.`,
            reasons: [
                "The property is currently under offer and visits have been temporarily suspended.",
                "If the property becomes available again, new visit slots may be opened and you will be able to submit a new visit request.",
            ],
        },
        "fr-BE": {
            subheading: `Votre visite confirmée pour le bien ${ESTATE.id} prévue le ${EVENT.date} à ${EVENT.time} a été annulée.`,
            reasons: [
                "Le bien est actuellement sous offre et les visites sont temporairement suspendues.",
                "Si le bien redevient disponible, de nouveaux créneaux de visite pourront être ouverts et vous pourrez soumettre une nouvelle demande.",
            ],
        },
        "nl-BE": {
            subheading: `Uw bevestigde bezoek voor woning ${ESTATE.id} gepland op ${EVENT.date} om ${EVENT.time} is geannuleerd.`,
            reasons: [
                "De woning staat momenteel onder bod en bezichtigingen zijn tijdelijk stopgezet.",
                "Als de woning opnieuw beschikbaar wordt, kunnen nieuwe bezoekmomenten worden geopend en kunt u een nieuwe aanvraag indienen.",
            ],
        },
        showRemoveBtn: false,
        showVisitDetails: false,
    },

    "Deactive_confirm": {
        "en-GB": {
            subheading: `Your confirmed visit scheduled for ${EVENT.date} at ${EVENT.time} has been cancelled.`,
            reasons: ["The property has been sold, rented, or removed from the market and is no longer available for visits."],
        },
        "fr-BE": {
            subheading: `Votre visite confirmée prévue le ${EVENT.date} à ${EVENT.time} a été annulée.`,
            reasons: ["Le bien a été vendu, loué ou retiré du marché et n'est plus disponible pour des visites."],
        },
        "nl-BE": {
            subheading: `Uw bevestigde bezoek gepland op ${EVENT.date} om ${EVENT.time} is geannuleerd.`,
            reasons: ["De woning is verkocht, verhuurd of van de markt gehaald en is niet langer beschikbaar voor bezichtigingen."],
        },
        showRemoveBtn: false,
        showVisitDetails: false,
    },
};

// ─── SHARED STYLES ───────────────────────────────────────────────
const st = {
    wrap: { backgroundColor: "#fff", border: "1px solid #D1D5DB", borderRadius: 16, width: "100%", margin: "0 auto", fontFamily: "Roboto, sans-serif" },
    inner: { backgroundColor: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, margin: "0 16px 24px 16px", overflow: "hidden" },
    hr: { border: "none", borderTop: "1px solid #E5E7EB", margin: 0 },
    p: { fontSize: 13, color: "#374151", margin: "0 0 8px 0", lineHeight: 1.7, fontFamily: "Roboto, sans-serif" },
    h5: { fontSize: 13, fontWeight: 700, color: "#111827", margin: 0, fontFamily: "Roboto, sans-serif" },
    lbl: { fontSize: 10, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.8px", margin: 0 },
    btn: { display: "inline-block", backgroundColor: "#274b97", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none", padding: "10px 22px", borderRadius: 8 },
};
const HR = () => <div style={{ padding: "0 20px" }}><hr style={st.hr} /></div>;

// ─── EMAIL TEMPLATE ──────────────────────────────────────────────
export const EmailTemplatesView = ({ lang = "en-GB", scenario = "OnHold_waiting" }) => {
    const t = translations[lang] || translations["en-GB"];
    const c = content[scenario];
    const txt = c[lang] || c["en-GB"];

    return (
        <div style={st.wrap}>

            {/* Logo */}
            <div style={{ textAlign: "center", padding: "28px 0 18px" }}>
                <img src={ESTATE.officeLogo} width="180" alt="logo" style={{ display: "block", margin: "0 auto" }} />
            </div>

            {/* Greeting */}
            <div style={{ padding: "0 28px 6px" }}>
                <p style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0, fontFamily: "Roboto, sans-serif" }}>
                    {t.Dear} <span>{CONTACT.firstName} {CONTACT.name}</span>,
                </p>
            </div>

            {/* Subheading */}
            <div style={{ padding: "4px 48px 22px 28px" }}>
                <p style={{ fontSize: 13, color: "#6B7280", margin: 0, lineHeight: 1.5, fontFamily: "Roboto, sans-serif" }}>
                    {txt.subheading}
                </p>
            </div>

            {/* Inner card */}
            <div style={st.inner}>

                {/* Section label */}
                <div style={{ padding: "14px 20px 8px" }}>
                    <p style={st.lbl}>{t.Resume}</p>
                </div>
                <div><hr style={st.hr} /></div>

                {/* Reason */}
                <div style={{ padding: "14px 20px 12px" }}>
                    <p style={{ ...st.h5, marginBottom: 8 }}>{t.Reason}</p>
                    {txt.reasons.map((line, i) => <p key={i} style={st.p}>{line}</p>)}
                </div>

                {/* Remove btn */}
                {c.showRemoveBtn && (
                    <div style={{ padding: "0 20px 18px", textAlign: "center" }}>
                        <a href={ESTATE.waitingListUrl} style={st.btn}>{t.RemoveBtn}</a>
                    </div>
                )}

                <HR />

                {/* Visit details */}
                {c.showVisitDetails && (
                    <>
                        <div style={{ padding: "14px 20px 16px" }}>
                            <p style={{ ...st.h5, marginBottom: 10 }}>{t.VisitDetails}</p>
                            <p style={st.p}><strong style={{ color: "#111827" }}>{t.Date}</strong> {EVENT.date}</p>
                            <p style={{ ...st.p, marginBottom: 0 }}><strong style={{ color: "#111827" }}>{t.Time}</strong> {EVENT.time}</p>
                        </div>
                        <HR />
                    </>
                )}

                {/* Property recap */}
                <div style={{ padding: "14px 20px 18px" }}>
                    <p style={{ ...st.h5, marginBottom: 12 }}>{t.PropertyRecap}</p>
                    <div style={{ display: "flex", gap: 16 }}>
                        <img src={ESTATE.imageUrl} width="160" height="105" style={{ borderRadius: 6, objectFit: "cover", flexShrink: 0 }} alt="property" />
                        <div>
                            <p style={{ fontSize: 13, fontWeight: 700, color: "#111827", margin: "0 0 8px", fontFamily: "Roboto, sans-serif" }}>{ESTATE.name}</p>
                            <p style={{ ...st.p, display: "flex", alignItems: "center", gap: 4 }}>📍 {ESTATE.address} {ESTATE.number}, {ESTATE.zip} {ESTATE.city}</p>
                            <p style={{ ...st.p, marginBottom: 0 }}>🛏 {ESTATE.rooms} &nbsp;|&nbsp; 🚿 {ESTATE.bathrooms} &nbsp;|&nbsp; 📐 {ESTATE.area}</p>
                        </div>
                    </div>
                </div>

                <HR />

                {/* Contact */}
                <div style={{ padding: "14px 20px 20px" }}>
                    <p style={{ ...st.h5, marginBottom: 8 }}>{t.ContactDetails}</p>
                    <p style={st.p}>{CONTACT.firstName} {CONTACT.name}</p>
                    <p style={{ ...st.p, color: "#2563EB" }}>{CONTACT.privateEmail}</p>
                    <p style={{ ...st.p, marginBottom: 0 }}>{CONTACT.privateMobile}</p>
                </div>

            </div>
        </div>
    );
};

// ─── DEMO ────────────────────────────────────────────────────────
//const SCENARIOS = [
//    { key: "OnHold_waiting", label: "Waiting + OnHold" },
//    { key: "Deactive_waiting", label: "Waiting + Deactive" },
//    { key: "OnHold_pending", label: "Pending + OnHold" },
//    { key: "Deactive_pending", label: "Pending + Deactive" },
//    { key: "OnHold_confirm", label: "Confirm + OnHold" },
//    { key: "Deactive_confirm", label: "Confirm + Deactive" },
//];

